import type { ReactNode } from "react";
import { PARAM_GROUPS, formatValue, valuesMatch } from "@/domain/calibration-params";
import type { NvpParameter, ParamGroup } from "@/domain/calibration-params";

interface GroupActions {
  base?: ReactNode;
  compare?: ReactNode;
}

interface ParamComparisonTableProps {
  baseParams: Map<string, NvpParameter>;
  baseLabel: string;
  compareParams?: Map<string, NvpParameter>;
  compareLabel?: string;
  diffColors?: { base: string; compare: string };
  /** What the boat holds right now: a third column, judged against compareParams. */
  liveParams?: Map<string, NvpParameter>;
  /** Per-group controls, rendered in the group's header row under their column. */
  groupActions?: (group: ParamGroup) => GroupActions;
}

const LIVE_DIFF_COLOR = "text-red-600 dark:text-red-400";
const HEADER_CELL = "text-right font-medium pb-1.5 whitespace-nowrap";
const GROUP_CELL = "pt-2.5 pb-1 text-right";

export function ParamComparisonTable({
  baseParams,
  baseLabel,
  compareParams,
  compareLabel,
  diffColors = { base: "text-green-700 dark:text-green-400", compare: "text-amber-600 dark:text-amber-400" },
  liveParams,
  groupActions,
}: ParamComparisonTableProps) {
  const hasCompare = !!compareParams;
  const hasLive = !!liveParams;
  const colCount = 2 + (hasCompare ? 1 : 0) + (hasLive ? 1 : 0);

  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-muted-foreground/70 text-[11px] uppercase tracking-wider">
          <th className="text-left font-medium pb-1.5 sticky left-0 bg-card">
            Parameter
          </th>
          <th className={HEADER_CELL}>{baseLabel}</th>
          {hasCompare && <th className={HEADER_CELL}>{compareLabel}</th>}
          {hasLive && <th className={HEADER_CELL}>Live</th>}
        </tr>
      </thead>
      <tbody>
        {PARAM_GROUPS.flatMap((group) => {
          const actions = groupActions?.(group);
          return [
            <tr key={`group-${group.label}`}>
              <td colSpan={actions ? 1 : colCount} className="pt-2.5 pb-1 sticky left-0 bg-card">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50">
                  {group.label}
                </span>
              </td>
              {actions && (
                <>
                  <td className={GROUP_CELL}>{actions.base}</td>
                  {hasCompare && <td className={GROUP_CELL}>{actions.compare}</td>}
                  {hasLive && <td />}
                </>
              )}
            </tr>,
            ...group.params.map((paramName) => {
              const baseParam = baseParams.get(paramName);
              const compareParam = compareParams?.get(paramName);
              const liveParam = liveParams?.get(paramName);
              const accuracy = compareParam?.Accuracy ?? baseParam?.Accuracy;
              const chpDataType = compareParam?.ChpDataType ?? baseParam?.ChpDataType;
              const isDiff = hasCompare && !valuesMatch(
                baseParam?.Value,
                compareParam?.Value,
                accuracy,
                chpDataType,
              );
              // Live is judged against the golden column when there is one,
              // else against the base column; an unread param is never a diff.
              const referenceParam = compareParam ?? baseParam;
              const liveRead = liveParam?.Value != null;
              const isLiveDiff = liveRead && !valuesMatch(
                referenceParam?.Value,
                liveParam.Value,
                liveParam.Accuracy ?? accuracy,
                liveParam.ChpDataType ?? chpDataType,
              );

              return (
                <tr key={paramName} className="border-t border-border/50">
                  <td className="py-1 font-mono text-foreground/80 sticky left-0 bg-card">
                    {paramName}
                  </td>
                  <td
                    className={`py-1 text-right font-mono whitespace-nowrap ${isDiff ? diffColors.base : "text-foreground/50"}`}
                  >
                    {baseParam ? formatValue(baseParam.Value, baseParam.Accuracy, baseParam.ChpDataType) : "-"}
                  </td>
                  {hasCompare && (
                    <td
                      className={`py-1 text-right font-mono whitespace-nowrap ${isDiff ? diffColors.compare : "text-foreground/50"}`}
                    >
                      {compareParam
                        ? formatValue(compareParam.Value, compareParam.Accuracy, compareParam.ChpDataType)
                        : "-"}
                    </td>
                  )}
                  {hasLive && (
                    <td
                      className={`py-1 text-right font-mono whitespace-nowrap ${isLiveDiff ? LIVE_DIFF_COLOR : "text-foreground/50"}`}
                    >
                      {liveRead
                        ? formatValue(liveParam.Value, liveParam.Accuracy, liveParam.ChpDataType)
                        : "-"}
                    </td>
                  )}
                </tr>
              );
            }),
          ];
        })}
      </tbody>
    </table>
  );
}
